import dayjs from 'dayjs';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Auth0Provider, AppState } from '@auth0/auth0-react';
import { ToastContainer } from 'react-toastify';
import styled from 'styled-components';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';
import './assets/rh_tooltip_bootstrap.css';

import App from './App';
import reportWebVitals from './reportWebVitals';
import './index.css';

import { risenHopeAPIClient } from './api/RisenHopeClient';
import { Environment } from './Environment';
import { persistor, store } from './model/store';
import { auth0Service } from './services/Auth0Service';
import { LocalLoggingService } from './services/LoggingService';
import { history } from './util/history';
import { colors } from './modules/core/Styles';
import { LoadingScreen } from './modules/core/LoadingScreen';
import { autoSaveService } from './services/FormAutoSaverService';

dayjs.extend(isSameOrAfter);

/**
 * Auth0 setup with CRA reference:
 * - https://github.com/auth0/auth0-react/tree/master/examples/cra-react-router
 * - https://auth0.com/blog/complete-guide-to-react-user-authentication/
 */

const onRedirectCallback = (appState: AppState | undefined) => {
  const urlParams = new URLSearchParams(window.location.search);
  const error = urlParams.get('error');
  const errorDescription = urlParams.get('error_description');
  const redirectToLogin = urlParams.get('redirectToLogin');

  if (error === 'access_denied' && errorDescription?.toLowerCase().includes('verify your email')) {
    history.replace('/verify-email');
    return;
  }

  if (redirectToLogin === 'true') {
    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin);
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

    if (domain && clientId) {
      let loginUrl = `https://${domain}/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=openid profile email`;
      if (audience) {
        loginUrl += `&audience=${encodeURIComponent(audience)}`;
      }
      loginUrl += `&screen_hint=login`;

      window.location.href = loginUrl;
      return;
    }
  }

  // If using a Hash Router, you need to use window.history.replaceState to
  // remove the `code` and `state` query parameters from the callback url.
  // window.history.replaceState({}, document.title, window.location.pathname);
  history.replace((appState && appState.returnTo) || window.location.pathname);
};

const startup = () => {
  // 1. Set the Environment.
  Environment.set({
    api: risenHopeAPIClient({
      baseURL: process.env.REACT_APP_API_BASE_URL!,
    }),
    services: {
      auth: auth0Service({ clientID: '', domain: '' }),
      logger: LocalLoggingService,
      formAutoSaver: autoSaveService(),
    },
  });

  ReactDOM.render(
    <Provider store={store}>
      <PersistGate loading={<LoadingScreen />} persistor={persistor}>
        <React.StrictMode>
          <Auth0Provider
            domain={process.env.REACT_APP_AUTH0_DOMAIN!}
            clientId={process.env.REACT_APP_AUTH0_CLIENT_ID!}
            redirectUri={window.location.origin}
            audience={process.env.REACT_APP_AUTH0_AUDIENCE}
            onRedirectCallback={onRedirectCallback}>
            <App />
            <StyledToast
              hideProgressBar={true}
              position="bottom-center"
              style={{ width: 'unset' }}
              closeButton={false}
            />
          </Auth0Provider>
        </React.StrictMode>
      </PersistGate>
    </Provider>,
    document.getElementById('root')
  );

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals();
};

const StyledToast = styled(ToastContainer).attrs({
  // custom props
})`
  /** Classes for the displayed toast **/
  .Toastify__toast {
    max-height: 60px;
    border-radius: 100px;
    justify-content: center;
    font-family: Nunito;
    font-weight: bold;
    font-size: 18px;
  }
  .Toastify__toast--error {
    background: ${colors.error};
  }
`;

startup();
